"use client";

import { useTranslations } from "next-intl";
import { useEffect } from "react";
import { useInView } from "react-intersection-observer";
import { Button } from "@/components/ui/button";

export interface InfiniteScrollQuery {
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  isFetching?: boolean;
  isFetchNextPageError?: boolean;
  error?: unknown;
  fetchNextPage: () => unknown;
}

interface InfiniteScrollProps {
  query: InfiniteScrollQuery;
  className?: string;
  enabled?: boolean;
}

/** Append-only pagination footer. Keep this after the list, never around it. */
export const InfiniteScroll = ({
  query,
  className = "py-4",
  enabled = true,
}: InfiniteScrollProps) => {
  const t = useTranslations("common");
  const { ref, inView } = useInView({ rootMargin: "200px" });
  const busy = query.isFetching || query.isFetchingNextPage;
  const failed = query.isFetchNextPageError || !!query.error;
  const { fetchNextPage, hasNextPage } = query;

  useEffect(() => {
    if (enabled && inView && hasNextPage && !busy && !failed) {
      void fetchNextPage();
    }
  }, [enabled, inView, hasNextPage, busy, failed, fetchNextPage]);

  if (!enabled || (!hasNextPage && !query.isFetchingNextPage)) return null;

  return (
    <div ref={ref} className={className} data-testid="infinite-scroll">
      {query.isFetchingNextPage ? (
        <div
          role="status"
          aria-label={t("loading")}
          className="flex items-center justify-center gap-2"
        >
          <div className="size-1 animate-pulse rounded-full bg-text-disabled/20" />
          <div className="h-3 w-24 animate-pulse rounded bg-text-disabled/20" />
          <div className="size-1 animate-pulse rounded-full bg-text-disabled/20" />
        </div>
      ) : failed ? (
        <Button
          variant="bordered"
          colors="primary"
          size="sm"
          disabled={busy}
          onClick={() => void fetchNextPage()}
        >
          {t("retry")}
        </Button>
      ) : null}
    </div>
  );
};
